import React from 'react';
import {
  EditorState,
  Modifier,
  SelectionState,
  RichUtils,
  getDefaultKeyBinding,
  KeyBindingUtil,
  convertToRaw,
} from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import _ from 'lodash';
import '../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './style.scss';
import { getCustomStyleMap, getSelectionCustomInlineStyle } from 'draftjs-utils';
import { EDITOR_TAB_WIDTH } from '../CommonUtilities/commonUtilities';
import {
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_OPTIONS,
  applyFontFamilyToSelection,
  applyFontSizeToSelection,
  editorContentEquals,
  fontFamilyToStyleName,
  fontSizeToStyleName,
  ensureIndentInheritsFollowingStyles,
  normalizeEditorWhitespace,
  normalizeFontFamilyStyleName,
  normalizeFontSizeStyleName,
} from '../CommonUtilities/editorStateUtils';

const blockStyleFn = (block) => {
  const textAlign = block.getData().get('text-align');
  if (textAlign) {
    return `rdw-${textAlign}-aligned-block`;
  }
  return '';
};

const customStyleMap = getCustomStyleMap();

/** Read merge-field names from editor text; avoid HTML export which can split placeholders across spans. */
const extractMergeFieldsFromEditorState = (editorState) => {
  if (!editorState?.getCurrentContent) {
    return [];
  }

  const plainText = editorState.getCurrentContent().getPlainText();
  return [...plainText.matchAll(/\{([^{}]+)\}/g)].map((match) => match[1]);
};

const customStyleFn = (styles) => {
  const styleObj = {};
  styles.forEach((style) => {
    const fontFamilyMatch = style.match(/^fontfamily-(.+)$/);
    if (fontFamilyMatch) {
      styleObj.fontFamily = fontFamilyMatch[1].replace(/-/g, ' ');
    }
  });
  return styleObj;
};

const inlineStylesChanged = (previousState, nextState) => {
  const previousContent = previousState.getCurrentContent();
  const nextContent = nextState.getCurrentContent();
  if (previousContent.getPlainText() !== nextContent.getPlainText()) {
    return false;
  }
  return previousContent !== nextContent;
};

const serializeFontFamilyRanges = (content) =>
  convertToRaw(content)
    .blocks.map((block) =>
      block.inlineStyleRanges
        .filter((range) => range.style.startsWith('fontfamily-'))
        .map((range) => `${range.style}:${range.offset}:${range.length}`)
        .join('|')
    )
    .join(';');

const fontFamilyFormattingChanged = (previousState, nextState) => {
  if (!inlineStylesChanged(previousState, nextState)) {
    return false;
  }

  const previousContent = previousState.getCurrentContent();
  const nextContent = nextState.getCurrentContent();
  return (
    serializeFontFamilyRanges(previousContent) !==
    serializeFontFamilyRanges(nextContent)
  );
};

const maybeApplyFontFamilyToSelection = (previousState, editorState) => {
  if (!fontFamilyFormattingChanged(previousState, editorState)) {
    return editorState;
  }

  const toolbarStyleNames = FONT_FAMILY_OPTIONS.map(fontFamilyToStyleName);
  let targetStyle = null;

  editorState.getCurrentInlineStyle().forEach((style) => {
    if (toolbarStyleNames.includes(style)) {
      targetStyle = style;
    } else if (style.startsWith('fontfamily-')) {
      targetStyle = normalizeFontFamilyStyleName(style);
    }
  });

  if (!targetStyle) {
    const fontFamilyStyle = getSelectionCustomInlineStyle(editorState, [
      'FONTFAMILY',
    ]).FONTFAMILY;
    if (fontFamilyStyle) {
      targetStyle = normalizeFontFamilyStyleName(String(fontFamilyStyle));
    }
  }

  if (!targetStyle) {
    return editorState;
  }

  return applyFontFamilyToSelection(editorState, targetStyle);
};

const FONT_SIZE_STYLE_PATTERN = /^fontsize-/i;

const serializeFontSizeRanges = (content) =>
  convertToRaw(content)
    .blocks.map((block) =>
      block.inlineStyleRanges
        .filter((range) => FONT_SIZE_STYLE_PATTERN.test(range.style))
        .map((range) => `${range.style}:${range.offset}:${range.length}`)
        .join('|')
    )
    .join(';');

const fontSizeFormattingChanged = (previousState, nextState) => {
  if (!inlineStylesChanged(previousState, nextState)) {
    return false;
  }

  const previousContent = previousState.getCurrentContent();
  const nextContent = nextState.getCurrentContent();
  return (
    serializeFontSizeRanges(previousContent) !==
    serializeFontSizeRanges(nextContent)
  );
};

const maybeApplyFontSizeToSelection = (previousState, editorState) => {
  if (!fontSizeFormattingChanged(previousState, editorState)) {
    return editorState;
  }

  const toolbarStyleNames = FONT_SIZE_OPTIONS.map(fontSizeToStyleName);
  let targetStyle = null;

  editorState.getCurrentInlineStyle().forEach((style) => {
    if (toolbarStyleNames.includes(style)) {
      targetStyle = style;
    } else if (FONT_SIZE_STYLE_PATTERN.test(style)) {
      targetStyle = normalizeFontSizeStyleName(style);
    }
  });

  if (!targetStyle) {
    const fontSizeStyle = getSelectionCustomInlineStyle(editorState, [
      'FONTSIZE',
    ]).FONTSIZE;
    if (fontSizeStyle) {
      targetStyle = normalizeFontSizeStyleName(String(fontSizeStyle));
    }
  }

  if (!targetStyle) {
    return editorState;
  }

  return applyFontSizeToSelection(editorState, targetStyle);
};

const TAB_INDENT = '\u00A0'.repeat(EDITOR_TAB_WIDTH);
const NBSP = '\u00A0';

const shouldInsertNbspForSpace = (editorState) => {
  const selection = editorState.getSelection();
  if (!selection.isCollapsed()) {
    return false;
  }

  const block = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey());
  const offset = selection.getStartOffset();
  const text = block.getText();

  if (offset === 0 || text[offset - 1] === '\n') {
    return true;
  }

  const lineStart = text.lastIndexOf('\n', offset - 1) + 1;
  const beforeCursor = text.slice(lineStart, offset);
  if (beforeCursor.length > 0 && /^[ \u00A0]+$/.test(beforeCursor)) {
    return true;
  }

  return text[offset - 1] === ' ' || text[offset - 1] === NBSP;
};

const insertCharAtSelection = (editorState, char) => {
  const selection = editorState.getSelection();
  const content = editorState.getCurrentContent();
  const newContent = Modifier.replaceText(content, selection, char);
  const pushedState = EditorState.push(
    editorState,
    newContent,
    'insert-characters'
  );

  return EditorState.forceSelection(
    pushedState,
    selection.merge({
      anchorOffset: selection.getStartOffset() + char.length,
      focusOffset: selection.getStartOffset() + char.length,
      isBackward: false,
    })
  );
};

const insertTabAtSelection = (editorState) => {
  const selection = editorState.getSelection();
  const content = editorState.getCurrentContent();
  const newContent = Modifier.replaceText(content, selection, TAB_INDENT);
  const pushedState = EditorState.push(
    editorState,
    newContent,
    'insert-characters'
  );
  const nextOffset = selection.getStartOffset() + TAB_INDENT.length;

  return EditorState.forceSelection(
    pushedState,
    selection.merge({
      anchorOffset: nextOffset,
      focusOffset: nextOffset,
      isBackward: false,
    })
  );
};

const outdentTabAtSelection = (editorState) => {
  const selection = editorState.getSelection();
  if (!selection.isCollapsed()) {
    return editorState;
  }

  const blockKey = selection.getStartKey();
  const block = editorState.getCurrentContent().getBlockForKey(blockKey);
  const offset = selection.getStartOffset();
  const text = block.getText();
  let removeStart = offset;
  let removed = 0;

  while (removeStart > 0 && removed < EDITOR_TAB_WIDTH) {
    const char = text[removeStart - 1];
    if (char === '\u00A0' || char === ' ') {
      removeStart -= 1;
      removed += 1;
    } else {
      break;
    }
  }

  if (removed === 0) {
    return editorState;
  }

  const removeSelection = selection.merge({
    anchorOffset: removeStart,
    focusOffset: offset,
  });
  const newContent = Modifier.replaceText(
    editorState.getCurrentContent(),
    removeSelection,
    ''
  );
  const pushedState = EditorState.push(
    editorState,
    newContent,
    'remove-range'
  );

  return EditorState.forceSelection(
    pushedState,
    selection.merge({
      anchorOffset: removeStart,
      focusOffset: removeStart,
      isBackward: false,
    })
  );
};

function EditorContainer(props) {
  const {
    result,
    insertData,
    filterAction,
    getTextFromHtml,
    deleteData,
    isEnablebyRole,
    editorStateChange,
  } = props;
  const edtState = { editorState: EditorState.createEmpty() };
  const [state, setState] = React.useState(edtState);
  const latestEditorStateRef = React.useRef(state.editorState);

  const syncEditorState = React.useCallback(
    (editorState) => {
      if (!filterAction || !editorState) {
        return;
      }

      const resultState = result && result.getCurrentContent ? result : null;
      if (resultState && editorContentEquals(editorState, resultState)) {
        return;
      }

      filterAction(editorState);
    },
    [filterAction, result]
  );

  const onEditorStateChange = (editorState) => {
    let nextState = maybeApplyFontFamilyToSelection(
      latestEditorStateRef.current,
      editorState
    );
    nextState = maybeApplyFontSizeToSelection(
      latestEditorStateRef.current,
      nextState
    );
    nextState = normalizeEditorWhitespace(nextState);
    nextState = ensureIndentInheritsFollowingStyles(nextState);
    latestEditorStateRef.current = nextState;

    if (editorStateChange) {
      const oldContent = state.editorState.getCurrentContent();
      const newContent = nextState.getCurrentContent();
      if (oldContent !== newContent) {
        editorStateChange(nextState);
      }
    }
    setState({ editorState: nextState });
    syncEditorState(nextState);
  };

  const onEditorStateBlur = (e, editorState) => {
    const currentEditorState = editorState || state.editorState;
    if (getTextFromHtml && currentEditorState) {
      getTextFromHtml(extractMergeFieldsFromEditorState(currentEditorState));
    }

    syncEditorState(currentEditorState);
  };

  const getHtmlOutput = React.useCallback((data) => {
    if (data && data !== '') {
      latestEditorStateRef.current = data;
      setState({ editorState: data });
      if (getTextFromHtml) {
        getTextFromHtml(extractMergeFieldsFromEditorState(data));
      }
    }
  }, []);

  React.useEffect(() => {
    if (result && result !== '' && !_.isEmpty(result)) {
      if (editorContentEquals(latestEditorStateRef.current, result)) {
        latestEditorStateRef.current = result;
        return;
      }
      getHtmlOutput(result);
    }
  }, [result]);

  const insertCharacter = (characterToInsert, editorState) => {
    const currentContent = editorState.getCurrentContent();
    const currentSelection = editorState.getSelection();

    const newContent = Modifier.replaceText(
      currentContent,
      currentSelection,
      characterToInsert
    );

    const newEditorState = EditorState.push(
      editorState,
      newContent,
      'insert-characters'
    );

    return newEditorState;
  };

  const findWithRegex = (regex, contentBlock, callback) => {
    const text = contentBlock.getText();
    let matchArr;
    let start;
    let end;
    while ((matchArr = regex.exec(text)) !== null) {
      start = matchArr.index;
      end = start + matchArr[0].length;
      callback(start, end);
    }
  };

  const removeCharacter = (characterToRemove, editorState) => {
    const regex = new RegExp(characterToRemove, 'g');
    const selectionsToReplace = [];
    const blockMap = editorState.getCurrentContent().getBlockMap();
    let text = null;
    let cBlock = null;
    blockMap.forEach((contentBlock) =>
      findWithRegex(regex, contentBlock, (start, end) => {
        const blockKey = contentBlock.getKey();
        text = contentBlock.getText();
        cBlock = contentBlock;
        const blockSelection = SelectionState.createEmpty(blockKey).merge({
          anchorOffset: start,
          focusOffset: end,
        });
        selectionsToReplace.push(blockSelection);
      })
    );

    let contentState = editorState.getCurrentContent();

    selectionsToReplace.forEach((selectionState) => {
      contentState = Modifier.replaceText(contentState, selectionState, '');
    });
    const newEditorState = EditorState.push(editorState, contentState);
    return newEditorState;
  };

  React.useEffect(() => {
    if (insertData && !_.isEmpty(insertData)) {
      let editState = [];
      _.map(insertData, (val) => {
        const newEditorState = insertCharacter(
          `{${val.displayName}}`,
          state.editorState
        );
        editState = newEditorState;
      });
      if (editState) {
        const extractParams = extractMergeFieldsFromEditorState(editState);
        if (!_.isEmpty(extractParams)) {
          getTextFromHtml(extractParams);
        }
      }
      filterAction(editState);
      setState({ editorState: editState });
    }
  }, [insertData]);

  React.useEffect(() => {
    if (deleteData && !_.isEmpty(deleteData)) {
      let editState = state.editorState;
      _.map(deleteData, (val) => {
        const newEditorState = removeCharacter(
          `{${val.displayName}}`,
          editState
        );
        editState = newEditorState;
      });
      if (editState) {
        getTextFromHtml(extractMergeFieldsFromEditorState(editState));
      }
      filterAction(editState);
      setState({ editorState: editState });
    }
  }, [deleteData]);

  const handleKeyCommand = (command, editorState) => {
    if (command === 'insert-tab') {
      onEditorStateChange(insertTabAtSelection(editorState));
      return 'handled';
    }
    if (command === 'insert-indent-space') {
      const spaceChar = shouldInsertNbspForSpace(editorState) ? NBSP : ' ';
      onEditorStateChange(insertCharAtSelection(editorState, spaceChar));
      return 'handled';
    }
    if (command === 'outdent-tab') {
      onEditorStateChange(outdentTabAtSelection(editorState));
      return 'handled';
    }
    if (command === 'split-block') {
      const newState = RichUtils.insertSoftNewline(editorState);
      if (newState) {
        onEditorStateChange(newState);
        return 'handled';
      }
    }
    return 'not-handled';
  };

  const keyBindingFn = (e) => {
    if (e.keyCode === 9) {
      return e.shiftKey ? 'outdent-tab' : 'insert-tab';
    }
    if (e.keyCode === 32 && !KeyBindingUtil.hasCommandModifier(e)) {
      return 'insert-indent-space';
    }
    if (e.keyCode === 13 && !KeyBindingUtil.hasCommandModifier(e)) {
      return 'split-block';
    }
    return getDefaultKeyBinding(e);
  };

  const handlePastedText = (text, html, editorState) => {
    // Convert the pasted text to plain text to remove formatting
    const plainText = text.replace(/<[^>]*>?/gm, '');

    // Replace regular spaces with non-breaking spaces
    const contentWithNbsp = plainText.replace(/ /g, '\u00A0');

    // Get current content and selection
    const currentContent = editorState.getCurrentContent();
    const currentSelection = editorState.getSelection();

    // Create new content with replaced text
    const newContent = Modifier.replaceText(
      currentContent,
      currentSelection,
      contentWithNbsp
    );
    const newEditorState = EditorState.push(
      editorState,
      newContent,
      'insert-characters'
    );
    onEditorStateChange(newEditorState);
    return 'handled';
  };

  // React.useEffect(() => {
  //   const fontFamily = getSelectionCustomInlineStyle(state.editorState, [
  //     'FONTFAMILY',
  //   ]).FONTFAMILY;
  //   const fontSize = getSelectionCustomInlineStyle(state.editorState, [
  //     'FONTSIZE',
  //   ]).FONTSIZE;
  //   console.log(
  //     'getCurrentInlineStyle---',
  //     state.editorState.getCurrentInlineStyle()
  //   );
  //   console.log(fontFamily + ' -- ' + fontSize);
  //   console.log('pass--', !fontSize || !fontFamily);

  //   if (!fontSize || !fontFamily) {
  //     let UpdateState = state.editorState;
  //     if (!fontSize) {
  //       UpdateState = ['fontsize-10'].reduce(
  //         (state1, style) => RichUtils.toggleInlineStyle(state1, style),
  //         UpdateState
  //       );
  //     }
  //     // if (!fontFamily) {
  //     //   UpdateState = ['fontfamily-Arial'].reduce(
  //     //     (state1, style) => RichUtils.toggleInlineStyle(state1, style),
  //     //     UpdateState
  //     //   );
  //     //   //   if (JSON.stringify(state.editorState) !== JSON.stringify(UpdateState)) {
  //     //   //     setState({ editorState: UpdateState });
  //     //   //   }
  //     // }
  //     if (JSON.stringify(state.editorState) !== JSON.stringify(UpdateState)) {
  //       setState({ editorState: UpdateState });
  //     }
  //   }
  // }, [state.editorState]);

  return (
    <div
      className='editor'
      style={{
        background: '#fff',
        border: '1px solid #aaafb9',
        height: props.height || '300px',
      }}
      onMouseDown={(event) => {
        const toolbar = event.currentTarget.querySelector('.rdw-editor-toolbar');
        if (toolbar && toolbar.contains(event.target)) {
          event.preventDefault();
        }
      }}
    >
      <Editor
        readOnly={!isEnablebyRole}
        editorState={state.editorState}
        onEditorStateChange={onEditorStateChange}
        onBlur={onEditorStateBlur}
        handleKeyCommand={handleKeyCommand}
        keyBindingFn={keyBindingFn}
        handlePastedText={handlePastedText}
        stripPastedStyles={true}
        customStyleMap={customStyleMap}
        customStyleFn={customStyleFn}
        blockStyleFn={blockStyleFn}
        toolbar={{
          options: [
            'blockType',
            'fontFamily',
            'fontSize',
            'inline',
            'textAlign',
            'list',
            'history',
          ],
          blockType: {
            inDropdown: true,
            options: [
              'Normal',
              'H1',
              'H2',
              'H3',
              'H4',
              'H5',
              'H6',
              'Blockquote',
              'Code',
            ],
            dropdownClassName: 'frr-rich-editor-dropdown',
          },
          fontFamily: {
            inDropdown: true,
            options: FONT_FAMILY_OPTIONS,
            className: 'rich-editor-font-family',
            dropdownClassName: 'frr-rich-editor-dropdown',
          },
          fontSize: {
            inDropdown: true,
            options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
            dropdownClassName: 'frr-rich-editor-dropdown',
          },
          inline: {
            options: ['bold', 'italic', 'underline'],
            bold: { className: 'testing-for-bold' },
          },
          list: {
            options: ['unordered', 'ordered'],
          },
          textAlign: { inDropdown: false },
        }}
      />
    </div>
  );
}

export default EditorContainer;
